import { NotificationOrigin, NotificationType, UploadState } from '@synergy/models';
import { createSelector } from 'reselect';
import { errorsToTouch, nullIfEmpty } from '@synergy/utils';

import { AppState } from '../../../../../../store/models/app-state';
import { SupplierDetailOverviewFormValues } from '../models/supplier-detail-overview-form-values';
import { SupplierDetailOverviewRequest } from '../api/models/supplier-detail-overview-request';

const supplierDetailOverviewState = (state: AppState) =>
    state.contactsAndOrganizations.suppliers.supplierDetail.overview;

export const supplierSelector = createSelector(
    supplierDetailOverviewState,
    detailOverviewState => detailOverviewState.supplier
);

export const supplierDetailOverviewBankAccountsSelector = createSelector(
    supplierDetailOverviewState,
    detailOverviewState => ({
        formValues: detailOverviewState.bankAccounts.map(bankAccount => ({
            ...bankAccount,
            media: bankAccount.media.map(m => ({ ...m, state: UploadState.Finished })),
        })),
        errors: detailOverviewState.bankAccountErrors,
    })
);

/**
 * The defaults the mask fills in for values the server left empty.
 *
 * Shared with the unsaved-changes baseline (#2159): compared against raw server
 * values, every one of these defaults would read as something the user typed.
 */
const withDefaults = (supplier?: SupplierDetailOverviewFormValues): SupplierDetailOverviewFormValues =>
    ({
        ...supplier,
        phones: supplier?.phones ?? [],
        tags: supplier?.tags ?? [],
        classifications: supplier?.classifications ?? [],
        website: supplier?.website ?? '',
        whitelistedCustomers: supplier?.whitelistedCustomers ?? [],
        addresses: supplier?.addresses ?? [],
        categoryIds: supplier?.categoryIds ?? [],
        emails: supplier?.emails ?? [],
        name: supplier?.name ?? '',
        ftpUsername: supplier?.ftpUsername ?? '',
        ftpPassword: supplier?.ftpPassword ?? '',
        newFtpPassword: '',
        uid: supplier?.uid ?? '',
        glnNumber: supplier?.glnNumber ?? '',
        internalNotes: supplier?.internalNotes ?? '',
        isLocked: supplier?.isLocked ?? false,
        mainEmail: supplier?.mainEmail ?? '',
        placeOfJurisdiction: supplier?.placeOfJurisdiction ?? '',
        registerNumber: supplier?.registerNumber ?? '',
        mainPhone: supplier?.mainPhone ?? '',
        xmlStoreFtpOption: supplier?.xmlStoreFtpOption ?? null,
        forwardPurchaseOrderXmlToLobster: supplier?.forwardPurchaseOrderXmlToLobster ?? false,
        isMain: supplier?.isMain ?? false,
        requireFunctionalAssets: supplier?.requireFunctionalAssets ?? false,
        financeAccountDebitor: supplier?.financeAccountDebitor ?? '',
        financeAccountCreditor: supplier?.financeAccountCreditor ?? '',
        notificationSettings: supplier?.notificationSettings ?? [
            {
                notificationOrigin: NotificationOrigin.PurchaseOrderManual,
                notificationType: NotificationType.EmailWithPdf,
                overrideEmail: false,
                recipientEmail: '',
            },
            {
                notificationOrigin: NotificationOrigin.PurchaseOrderShop,
                notificationType: NotificationType.EmailWithPdf,
                overrideEmail: false,
                recipientEmail: '',
            },
            {
                notificationOrigin: NotificationOrigin.Demand,
                notificationType: NotificationType.EmailWithPdf,
                overrideEmail: false,
                recipientEmail: '',
            },
            {
                notificationOrigin: NotificationOrigin.Inquiry,
                notificationType: NotificationType.EmailWithPdf,
                overrideEmail: false,
                recipientEmail: '',
            },
            {
                notificationOrigin: NotificationOrigin.Invoice,
                notificationType: NotificationType.EmailWithPdf,
                overrideEmail: false,
                recipientEmail: '',
            },
            {
                notificationOrigin: NotificationOrigin.Import,
                notificationType: NotificationType.EmailWithPdf,
                overrideEmail: false,
                recipientEmail: '',
            },
        ],
    } as SupplierDetailOverviewFormValues);

export const supplierDetailOverviewFormDataSelector = createSelector(
    supplierDetailOverviewState,
    detailOverviewState => {
        const { supplier, overviewErrors: errors } = detailOverviewState;

        return {
            formValues: withDefaults(supplier),
            errors,
            touched: errorsToTouch(errors),
        };
    }
);

/** What the mask compares against to decide whether anything is unsaved. */
export const supplierDetailOverviewBaselineSelector = createSelector(supplierDetailOverviewState, detailOverviewState =>
    detailOverviewState.baseline ? withDefaults(detailOverviewState.baseline) : undefined
);

export const supplierDetailOverviewRequestSelector = createSelector(
    supplierDetailOverviewState,
    detailOverviewState => {
        const supplier = detailOverviewState.supplier;

        if (!supplier) {
            return;
        }

        const {
            glnNumber,
            parent,
            uid,
            registerNumber,
            placeOfJurisdiction,
            internalNotes,
            whitelistedCustomers,
            responsibleContact,
            tags,
            classifications,
            ...data
        } = supplier;

        const phones = data.phones.map(phone => ({
            ...phone,
            id: phone.id && phone.id < 0 ? undefined : phone.id,
        }));

        const emails = data.emails.map(email => ({
            ...email,
            id: email.id && email.id < 0 ? undefined : email.id,
        }));

        const addresses = data.addresses.map(address => ({
            ...address,
            id: address.id && address.id < 0 ? undefined : address.id,
            country: address.country?.code,
            costCentre: nullIfEmpty(address.costCentre),
            regionCode: address.region?.code,
        }));

        const notifications = data.notificationSettings.map(notification => ({
            id: notification.id && notification.id < 0 ? undefined : notification.id,
            recipientEmail: notification.overrideEmail ? notification.recipientEmail : null,
            notificationType: notification.notificationType,
            notificationOrigin: notification.notificationOrigin,
        }));

        const request: SupplierDetailOverviewRequest = {
            name: data.name,
            glnNumber: nullIfEmpty(glnNumber),
            parentId: parent?.id ?? null,
            uid: nullIfEmpty(uid),
            registerNumber: nullIfEmpty(registerNumber),
            placeOfJurisdiction: nullIfEmpty(placeOfJurisdiction),
            internalNotes: nullIfEmpty(internalNotes),
            whitelistedCustomerIds: whitelistedCustomers?.map(customer => customer.id) ?? [],
            responsibleContactId: responsibleContact?.id ?? null,
            tagIds: tags?.map(tag => tag.id) ?? [],
            classificationIds: classifications?.map(classification => classification.id) ?? [],
            website: nullIfEmpty(data.website),
            mainEmail: data?.mainEmail ?? '',
            emails: emails,
            mainPhone: nullIfEmpty(data?.mainPhone),
            phones: phones,
            categoryIds: data?.categoryIds ?? [],
            addresses,
            ftpPassword: nullIfEmpty(data.ftpPassword),
            xmlStoreFtpOption: data.xmlStoreFtpOption ?? null,
            notificationSettings: notifications,
            forwardPurchaseOrderXmlToLobster: data.forwardPurchaseOrderXmlToLobster,
            isMain: data.isMain,
            requireFunctionalAssets: data.requireFunctionalAssets ?? false,
            financeAccountDebitor: nullIfEmpty(data.financeAccountDebitor),
            financeAccountCreditor: nullIfEmpty(data.financeAccountCreditor),
        };

        return request;
    }
);
